import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NodeProps, Handle, Position } from 'reactflow';
import { Hash, Calendar, Settings } from 'lucide-react';
import BotBuilderNode from '../BotBuilderNode/BotBuilderNode';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import CalculationTreeNode from '../../../../types/BotBuilder/CalculationTreeNode';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import SlotValueType from '../../../../types/BotBuilder/SlotValueType';
import IntentSlot from '../../../../types/BotBuilder/IntentSlot';
import Slot from '../../../../types/BotBuilder/Slot';
import ArithmeticOperation, {
  ArithmeticOperationLabels
} from '../../../../types/BotBuilder/ArithmeticOperation';
import DateOperation, {
  DateOperationLabels
} from '../../../../types/BotBuilder/DateOperation';
import ValueSource from '../../../../types/BotBuilder/ValueSource';
import useDebouncedEffect from '../../../../hooks/useDebouncedEffect';
import CustomInput from '../../../../components/CustomInput/CustomInput';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';
import { SystemVariableDto } from '../../../../services/SystemVariableService/SystemVariableService';

const getVariableKey = (
  variableName: string,
  systemVariables: SystemVariableDto[]
): string => {
  const isSystemVar = systemVariables.some((v) => v.name === variableName);
  return isSystemVar ? `chatbotContext.system.${variableName}` : variableName;
};

const getSecondOperandValue = (
  secondOperandSource: 'constant' | 'variable',
  selectedSecondVariable: string,
  secondOperand: string,
  systemVariables: SystemVariableDto[]
): string => {
  if (secondOperandSource === 'variable') {
    const isSystemVar = systemVariables.some(
      (v) => v.name === selectedSecondVariable
    );
    return isSystemVar
      ? `chatbotContext.system.${selectedSecondVariable}`
      : selectedSecondVariable;
  }
  return secondOperand;
};

export type CalculationNodeData = {
  node: BotResponseTreeNode;
  isLocked?: boolean;
};

const isCalculationTreeNode = (
  content: any
): content is CalculationTreeNode => {
  return content instanceof CalculationTreeNode;
};

const getVariableIcon = (variable: any): React.ReactElement => {
  if (variable.isSystemVariable) {
    return <Settings className='h-4 w-4 text-blue-600' />;
  }
  if (variable.valueType === SlotValueType.NUMBER) {
    return <Hash className='h-4 w-4' />;
  }
  return <Calendar className='h-4 w-4' />;
};

const getVariableBadge = (variable: any): React.ReactElement => {
  const isSystem = variable.isSystemVariable;
  const badgeClass = `text-xs px-2 py-1 rounded ${
    isSystem ? 'bg-blue-100 text-blue-800' : 'bg-teal-100 text-teal-800'
  }`;

  if (isSystem) {
    return <span className={badgeClass}>System</span>;
  }

  const label = variable.valueType === SlotValueType.NUMBER ? 'Number' : 'Date';
  return <span className={badgeClass}>{label}</span>;
};

function CalculationNode(
  props: NodeProps<CalculationNodeData>
): React.ReactElement {
  const { data, isConnectable } = props;
  const {
    intentSlotMap,
    intent,
    onNodeUpdate,
    onNodeDelete,
    slots,
    systemVariables
  } = useContext(BotBuilderContext);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  const calculationNode = isCalculationTreeNode(data.node.content)
    ? data.node.content
    : new CalculationTreeNode(null);

  const [selectedVariable, setSelectedVariable] = useState<string>(() => {
    const value =
      calculationNode.arithmeticStep?.operand1Value ||
      calculationNode.dateStep?.dateBaseValue ||
      '';
    if (value.startsWith('chatbotContext.system.')) {
      return value.replace('chatbotContext.system.', '');
    }
    return value;
  });

  const [arithmeticOperation, setArithmeticOperation] =
    useState<ArithmeticOperation>(
      calculationNode.arithmeticStep?.operation || ArithmeticOperation.ADD
    );

  const [dateOperation, setDateOperation] = useState<DateOperation>(
    calculationNode.dateStep?.dateOperation || DateOperation.ADD_DAYS
  );

  const [secondOperand, setSecondOperand] = useState<string>(
    calculationNode.arithmeticStep?.operand2Value ||
      calculationNode.dateStep?.dateAmountValue ||
      ''
  );

  const [secondOperandSource, setSecondOperandSource] = useState<
    'constant' | 'variable'
  >(
    calculationNode.arithmeticStep?.operand2Source === ValueSource.CONTEXT ||
      calculationNode.dateStep?.dateAmountSource === ValueSource.CONTEXT
      ? 'variable'
      : 'constant'
  );

  const [selectedSecondVariable, setSelectedSecondVariable] = useState<string>(
    () => {
      if (
        calculationNode.arithmeticStep?.operand2Source ===
          ValueSource.CONTEXT ||
        calculationNode.dateStep?.dateAmountSource === ValueSource.CONTEXT
      ) {
        const value =
          calculationNode.arithmeticStep?.operand2Value ||
          calculationNode.dateStep?.dateAmountValue ||
          '';
        if (value.startsWith('chatbotContext.system.')) {
          return value.replace('chatbotContext.system.', '');
        }
        return value;
      }
      return '';
    }
  );

  const [localResultSlotName, setLocalResultSlotName] = useState<string>(() => {
    const arithmeticKey = calculationNode.arithmeticStep?.resultKey;
    const dateKey = calculationNode.dateStep?.dateResultKey;
    if (arithmeticKey) {
      return arithmeticKey;
    }
    if (dateKey) {
      return dateKey;
    }

    return '';
  });

  const [selectedResultSlot, setSelectedResultSlot] = useState<Slot | null>(
    null
  );
  const isInitialLoad = useRef(true);

  const currentResultSlot = useMemo(() => {
    if (!localResultSlotName) return null;
    return intentSlotMap[localResultSlotName] || null;
  }, [localResultSlotName, intentSlotMap]);

  useEffect(() => {
    if (
      !localResultSlotName ||
      !intent?.slots ||
      !slots ||
      !selectedResultSlot
    ) {
      return;
    }

    const intentSlot = intent.slots.find(
      (slot) => slot.slotName === localResultSlotName
    );

    if (intentSlot) {
      const globalSlot = slots.find(
        (slot) => slot.slotName === intentSlot.slotType
      );
      if (globalSlot && globalSlot.slotName !== selectedResultSlot.slotName) {
        setSelectedResultSlot(globalSlot);
      }
    }
  }, [slots]);

  useEffect(() => {
    if (
      !localResultSlotName ||
      !intent?.slots ||
      !slots ||
      selectedResultSlot
    ) {
      return;
    }

    const intentSlot = intent.slots.find(
      (slot) => slot.slotName === localResultSlotName
    );

    if (intentSlot) {
      const globalSlot = slots.find(
        (slot) => slot.slotName === intentSlot.slotType
      );
      if (globalSlot) {
        setSelectedResultSlot(globalSlot);
      }
    }
  }, [localResultSlotName, intent, slots, selectedResultSlot]);

  useEffect(() => {
    if (!calculationNode) return;
    if (
      currentResultSlot &&
      calculationNode.resultIntentSlotId !== currentResultSlot.intentSlotId
    ) {
      calculationNode.resultIntentSlotId = currentResultSlot.intentSlotId;
      calculationNode.resultSlotType = currentResultSlot.slotName;
      onNodeUpdate(data.node);
    }
  }, [calculationNode, currentResultSlot]);

  useEffect(() => {
    if (!intent || !calculationNode) return;

    if (!calculationNode.resultSlotType && localResultSlotName) {
      const intentSlot = intent.slots.find(
        (islot) => islot.slotName === localResultSlotName
      );
      if (intentSlot) {
        calculationNode.resultSlotType = intentSlot.slotType;
        calculationNode.resultIntentSlotId = intentSlot.id;
        onNodeUpdate(data.node);
      }
    }
  }, [intent, calculationNode, data.node, localResultSlotName]);

  useEffect(() => {
    if (!calculationNode?.resultSlotType) return;

    const latestSlot = slots.find(
      (slot) => slot.slotName === calculationNode.resultSlotType
    );

    if (latestSlot) {
      setSelectedResultSlot(latestSlot);
    } else if (currentResultSlot) {
      setSelectedResultSlot(currentResultSlot);
    }
  }, [slots, calculationNode?.resultSlotType, currentResultSlot]);

  useEffect(() => {
    if (
      selectedResultSlot &&
      localResultSlotName &&
      intent &&
      calculationNode
    ) {
      const existingIntentSlot = intent.slots.find(
        (slot) => slot.slotName === localResultSlotName
      );

      if (!existingIntentSlot) {
        const newIntentSlot: IntentSlot = {
          id: null,
          slotName: localResultSlotName,
          slotType: selectedResultSlot.slotName
        };
        intent.slots.push(newIntentSlot);
      } else if (existingIntentSlot.slotType !== selectedResultSlot.slotName) {
        existingIntentSlot.slotType = selectedResultSlot.slotName;
      }

      if (calculationNode.resultSlotType !== selectedResultSlot.slotName) {
        calculationNode.resultSlotType = selectedResultSlot.slotName;
        calculationNode.resultSlotName = localResultSlotName;
      }
    }
  }, [selectedResultSlot, localResultSlotName, intent, calculationNode]);

  useEffect(() => {
    if (
      isInitialLoad.current &&
      calculationNode?.resultSlotType &&
      !selectedResultSlot &&
      slots?.length > 0
    ) {
      const existingSlot = slots.find(
        (slot) => slot.slotName === calculationNode.resultSlotType
      );
      if (existingSlot) {
        setSelectedResultSlot(existingSlot);
      }
      isInitialLoad.current = false;
    }
  }, [calculationNode?.resultSlotType, selectedResultSlot, slots]);

  const availableVariables = useMemo(() => {
    if (!intent || !intentSlotMap) return [];

    const intentSlots = intent.slots
      .filter((slot: IntentSlot) => {
        const slotData = intentSlotMap[slot.slotName];
        const valueType = slotData?.valueType ?? slot.inlineValueType;
        return (
          valueType === SlotValueType.NUMBER || valueType === SlotValueType.DATE
        );
      })
      .map((slot: IntentSlot) => ({
        ...slot,
        valueType:
          intentSlotMap[slot.slotName]?.valueType ?? slot.inlineValueType,
        isSystemVariable: false
      }));

    const systemVars = systemVariables
      .filter(
        (variable) =>
          variable.valueType === SlotValueType.NUMBER ||
          variable.valueType === SlotValueType.DATE
      )
      .map((variable) => ({
        slotName: variable.name,
        slotType: variable.name,
        valueType: variable.valueType,
        isSystemVariable: true,
        description: variable.description
      }));

    return [...intentSlots, ...systemVars];
  }, [intent, intentSlotMap]);

  const selectedVariableType = useMemo(() => {
    if (!selectedVariable) return null;

    const systemVariable = systemVariables.find(
      (v) => v.name === selectedVariable
    );
    if (systemVariable) {
      return systemVariable.valueType;
    }

    if (intentSlotMap[selectedVariable]) {
      return intentSlotMap[selectedVariable].valueType;
    }

    const inlineSlot = intent?.slots.find(
      (s) => s.slotName === selectedVariable
    );
    if (inlineSlot?.inlineValueType) {
      return inlineSlot.inlineValueType;
    }

    return null;
  }, [selectedVariable, intentSlotMap, intent]);

  const availableResultSlots = useMemo(() => {
    if (!slots || !selectedVariableType) return [];

    return slots.filter(
      (slot: Slot) => slot.valueType === selectedVariableType
    );
  }, [slots, selectedVariableType]);

  const updateIntentSlot = (slotName: string, slotType: string): void => {
    if (!intent) return;

    const existingIntentSlot = intent.slots.find(
      (slot) => slot.slotName === slotName
    );

    if (!existingIntentSlot) {
      const newIntentSlot: IntentSlot = {
        id: null,
        slotName,
        slotType
      };
      intent.slots.push(newIntentSlot);
    } else {
      existingIntentSlot.slotType = slotType;
    }
  };

  const handleResultSlotNameBlur = (): void => {
    if (!localResultSlotName || !selectedResultSlot || !calculationNode) {
      return;
    }

    updateIntentSlot(localResultSlotName, selectedResultSlot.slotName);

    calculationNode.resultSlotName = localResultSlotName;
    calculationNode.resultSlotType = selectedResultSlot.slotName;
    const mapped = intentSlotMap[localResultSlotName];
    if (mapped?.intentSlotId) {
      calculationNode.resultIntentSlotId = mapped.intentSlotId;
    }
    onNodeUpdate(data.node);
  };

  const handleResultSlotChange = (newSlot: Slot | null): void => {
    setSelectedResultSlot(newSlot);
    if (!calculationNode) return;

    if (!newSlot) {
      calculationNode.resultSlotName = null;
      calculationNode.resultSlotType = null;
      calculationNode.resultIntentSlotId = null;
      setLocalResultSlotName('');
      onNodeUpdate(data.node);
      return;
    }

    if (!localResultSlotName) return;

    updateIntentSlot(localResultSlotName, newSlot.slotName);

    calculationNode.resultSlotName = localResultSlotName;
    calculationNode.resultSlotType = newSlot.slotName;
    const mapped = intentSlotMap[localResultSlotName];
    if (mapped?.intentSlotId) {
      calculationNode.resultIntentSlotId = mapped.intentSlotId;
    }
    onNodeUpdate(data.node);
  };

  const updateCalculationNode = (): void => {
    if (!selectedVariable || !localResultSlotName || !selectedResultSlot)
      return;

    if (secondOperandSource === 'constant' && !secondOperand) return;
    if (secondOperandSource === 'variable' && !selectedSecondVariable) return;

    const updatedNode = new CalculationTreeNode(
      calculationNode.nextStep,
      null,
      null,
      localResultSlotName,
      selectedResultSlot.slotName,
      calculationNode.resultSlotType === selectedResultSlot.slotName
        ? calculationNode.resultIntentSlotId
        : null
    );

    if (selectedVariableType === SlotValueType.NUMBER) {
      updatedNode.arithmeticStep = {
        operation: arithmeticOperation,
        operand1Source: ValueSource.CONTEXT,
        operand1Value: getVariableKey(selectedVariable, systemVariables),
        operand2Source:
          secondOperandSource === 'variable'
            ? ValueSource.CONTEXT
            : ValueSource.CONSTANT,
        operand2Value: getSecondOperandValue(
          secondOperandSource,
          selectedSecondVariable,
          secondOperand,
          systemVariables
        ),
        resultKey: localResultSlotName
      };
      updatedNode.dateStep = null;
    } else if (selectedVariableType === SlotValueType.DATE) {
      updatedNode.dateStep = {
        dateBaseSource: ValueSource.CONTEXT,
        dateBaseValue: getVariableKey(selectedVariable, systemVariables),
        dateOperation,
        dateAmountSource:
          secondOperandSource === 'variable'
            ? ValueSource.CONTEXT
            : ValueSource.CONSTANT,
        dateAmountValue: getSecondOperandValue(
          secondOperandSource,
          selectedSecondVariable,
          secondOperand,
          systemVariables
        ),
        dateResultKey: localResultSlotName,
        dateFormat: 'yyyy-MM-dd'
      };
      updatedNode.arithmeticStep = null;
    }

    const updatedTreeNode = { ...data.node, content: updatedNode };
    onNodeUpdate(updatedTreeNode);
  };

  useDebouncedEffect(
    updateCalculationNode,
    [
      selectedVariable,
      arithmeticOperation,
      dateOperation,
      secondOperand,
      secondOperandSource,
      selectedSecondVariable,
      localResultSlotName,
      selectedVariableType
    ],
    500
  );

  const onDelete = async (): Promise<void> => {
    onNodeDelete(data.node);
  };

  return (
    <BotBuilderNode
      title='Calculation'
      deleteButtonRef={deleteButtonRef}
      onNodeDelete={onDelete}
      sourceNode={data.node}
      isLocked={data.isLocked}
    >
      <div className='flex flex-col gap-2 pb-4'>
        <CustomSelect
          label='Select input variable'
          value={selectedVariable}
          onValueChange={setSelectedVariable}
          placeholder='Choose a number or date variable'
          className='bg-white'
          disabled={data.isLocked}
          options={availableVariables.map((variable) => ({
            value: variable.slotName,
            label: (
              <div className='flex items-center gap-2'>
                {getVariableIcon(variable)}
                <span>{variable.slotName}</span>
                {getVariableBadge(variable)}
              </div>
            )
          }))}
        />

        {selectedVariable && selectedVariableType && (
          <CustomSelect
            label='Operation'
            value={
              selectedVariableType === SlotValueType.NUMBER
                ? arithmeticOperation
                : dateOperation
            }
            onValueChange={(value) => {
              if (selectedVariableType === SlotValueType.NUMBER) {
                setArithmeticOperation(value as ArithmeticOperation);
              } else {
                setDateOperation(value as DateOperation);
              }
            }}
            className='bg-white'
            disabled={data.isLocked}
            options={
              selectedVariableType === SlotValueType.NUMBER
                ? [
                    {
                      value: ArithmeticOperation.ADD,
                      label: ArithmeticOperationLabels[ArithmeticOperation.ADD]
                    },
                    {
                      value: ArithmeticOperation.SUBTRACT,
                      label:
                        ArithmeticOperationLabels[ArithmeticOperation.SUBTRACT]
                    },
                    {
                      value: ArithmeticOperation.MULTIPLY,
                      label:
                        ArithmeticOperationLabels[ArithmeticOperation.MULTIPLY]
                    },
                    {
                      value: ArithmeticOperation.DIVIDE,
                      label:
                        ArithmeticOperationLabels[ArithmeticOperation.DIVIDE]
                    }
                  ]
                : [
                    {
                      value: DateOperation.ADD_DAYS,
                      label: DateOperationLabels[DateOperation.ADD_DAYS]
                    },
                    {
                      value: DateOperation.SUBTRACT_DAYS,
                      label: DateOperationLabels[DateOperation.SUBTRACT_DAYS]
                    }
                  ]
            }
          />
        )}

        {selectedVariable && selectedVariableType && (
          <>
            <CustomSelect
              label='Value type'
              value={secondOperandSource}
              onValueChange={(value) =>
                setSecondOperandSource(value as 'constant' | 'variable')
              }
              className='bg-white'
              disabled={data.isLocked}
              options={[
                { value: 'constant', label: 'Constant value' },
                { value: 'variable', label: 'Variable' }
              ]}
            />

            {secondOperandSource === 'constant' ? (
              <CustomInput
                label={
                  selectedVariableType === SlotValueType.NUMBER
                    ? 'Number'
                    : 'Days'
                }
                type='number'
                value={secondOperand}
                name='second-operand'
                id='second-operand'
                onChange={(e) => {
                  e.stopPropagation();
                  setSecondOperand(e.target.value || '');
                }}
                onBlur={() => {}}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                className='bg-white'
                min='0'
                disabled={data.isLocked}
              />
            ) : (
              <CustomSelect
                label={
                  selectedVariableType === SlotValueType.NUMBER
                    ? 'Select number variable'
                    : 'Select days variable'
                }
                value={selectedSecondVariable}
                onValueChange={setSelectedSecondVariable}
                placeholder='Choose a variable'
                className='bg-white'
                disabled={data.isLocked}
                options={availableVariables
                  .filter((variable) => {
                    if (variable.valueType !== selectedVariableType)
                      return false;
                    return variable.slotName !== selectedVariable;
                  })
                  .map((variable) => ({
                    value: variable.slotName,
                    label: (
                      <div className='flex items-center gap-2'>
                        {getVariableIcon(variable)}
                        <span>{variable.slotName}</span>
                        {getVariableBadge(variable)}
                      </div>
                    )
                  }))}
              />
            )}
          </>
        )}

        {selectedVariable && selectedVariableType && (
          <>
            <CustomSelect
              label='Output variable type'
              value={selectedResultSlot?.slotName || ''}
              onValueChange={(value) => {
                const slot =
                  availableResultSlots.find((s) => s.slotName === value) ||
                  null;
                handleResultSlotChange(slot);
              }}
              placeholder='Choose a global variable'
              className='bg-white'
              disabled={data.isLocked}
              options={availableResultSlots.map((slot) => ({
                value: slot.slotName,
                label: (
                  <div className='flex items-center gap-2'>
                    {slot.valueType === SlotValueType.NUMBER ? (
                      <Hash className='h-4 w-4' />
                    ) : (
                      <Calendar className='h-4 w-4' />
                    )}
                    <span>{slot.slotName}</span>
                    <span className='text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded'>
                      {slot.valueType === SlotValueType.NUMBER
                        ? 'Number'
                        : 'Date'}
                    </span>
                  </div>
                )
              }))}
            />
            <CustomInput
              label='Output variable'
              type='text'
              value={localResultSlotName}
              name='result-variable-name'
              id='result-variable-name'
              placeholder='Enter a variable name to store the output'
              onChange={(e) => {
                e.stopPropagation();
                setLocalResultSlotName(e.target.value || '');
              }}
              onBlur={handleResultSlotNameBlur}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              className='bg-white'
              disabled={data.isLocked || !selectedResultSlot}
            />
          </>
        )}
      </div>

      <div className='-mb-2'>
        <Handle
          type='target'
          position={Position.Top}
          style={{ top: -10 }}
          isConnectable={isConnectable}
          id='a'
        />
        <Handle
          type='source'
          position={Position.Bottom}
          id='b'
          style={{ bottom: -10 }}
          isConnectable={isConnectable}
        />
      </div>
    </BotBuilderNode>
  );
}

export default CalculationNode;
